import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './PaymentAnalyticsReport.css';

const PaymentAnalyticsReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/reports/payments/?month=${filters.month}&year=${filters.year}`);
      
      if (response.data && response.data.success) {
        setReportData(response.data.data);
        toastSuccess('Report loaded successfully');
      } else {
        toastError('Failed to load report');
      }
    } catch (error) {
      console.error('Error loading payment analytics report:', error);
      toastError('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: parseInt(value) }));
  };

  const handleRefresh = () => {
    loadReport();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear - 2; year <= currentYear + 1; year++) {
    yearOptions.push({ value: year, label: year.toString() });
  }

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div className="text-center mt-5">
            <RTSpinner message="Loading payment analytics report..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Payment Analytics Report</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <RTButton
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/reports')}
            >
              ← Back
            </RTButton>
            <RTButton
              variant="primary"
              size="sm"
              onClick={handleRefresh}
            >
              Refresh
            </RTButton>
          </div>
        </div>

        {/* Filter Section */}
        <div className="rt-section" style={{ flex: 'none' }}>
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Month</label>
              <select
                className="form-select"
                value={filters.month}
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Year</label>
              <select
                className="form-select"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                {yearOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <RTButton variant="primary" onClick={handleRefresh}>
                Apply Filters
              </RTButton>
            </div>
          </div>
        </div>

        {reportData && (
          <div className="payment-analytics-content">
            {/* Summary Stat Boxes */}
            <div className="stat-boxes">
              <div className="stat-box">
                <div className="stat-label">Total Payments</div>
                <div className="stat-count">{reportData.total_payments.count}</div>
                <div className="stat-amount">{formatCurrency(reportData.total_payments.amount)}</div>
              </div>
              <div className="stat-box accent-green">
                <div className="stat-label">Net Amount</div>
                <div className="stat-amount large">{formatCurrency(reportData.net_amount)}</div>
                <div className="stat-sub">After adjustments</div>
              </div>
              <div className="stat-box">
                <div className="stat-label">Advance Collected</div>
                <div className="stat-count">{reportData.advance_payments.count}</div>
                <div className="stat-amount">{formatCurrency(reportData.advance_payments.amount)}</div>
              </div>
              <div className="stat-box accent-amber">
                <div className="stat-label">Adjustments</div>
                <div className="stat-count">{reportData.adjustments.count}</div>
                <div className="stat-amount">{formatCurrency(reportData.adjustments.amount)}</div>
              </div>
            </div>

            {/* Daily Breakdown Chart */}
            <div className="analytics-section">
              <h3>Daily Payment Breakdown</h3>
              {reportData.daily_breakdown && reportData.daily_breakdown.length > 0 ? (
                <div className="bar-chart-container">
                  <div className="bar-chart">
                    {(() => {
                      const counts = reportData.daily_breakdown.map(d => d.count);
                      const maxCount = Math.max(...counts);
                      const minCount = Math.min(...counts);
                      
                      return reportData.daily_breakdown.map((day, index) => {
                        // Calculate height percentage using min-max scaling
                        // If all values are the same, show at 80% height
                        let heightPercentage;
                        if (maxCount === minCount) {
                          heightPercentage = 80;
                        } else {
                          // Scale between 20% and 95% based on min-max range
                          const normalized = (day.count - minCount) / (maxCount - minCount);
                          heightPercentage = 20 + (normalized * 75);
                        }
                        
                      return (
                        <div key={index} className="bar-wrapper">
                          <div className="bar-amount">{formatCurrency(day.amount)}</div>
                          <div className="bar-value">{day.count}</div>
                          <div 
                            className="bar daily-bar"
                            style={{ height: `${heightPercentage}%` }}
                            title={`${day.date}: ${day.count} payments - ${formatCurrency(day.amount)}`}
                          ></div>
                          <div className="bar-label">{day.date}</div>
                        </div>
                      );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                <div className="no-data">No payment data available for this period</div>
              )}
            </div>

            {/* Payment Timing Analysis */}
            <div className="analytics-section">
              <h3>Payment Timing Analysis</h3>
              <div className="timing-grid">
                <div className="timing-card early">
                  <div className="timing-icon">⚡</div>
                  <div className="timing-label">Early (≤2 days)</div>
                  <div className="timing-count">{reportData.payment_timing.early}</div>
                </div>
                <div className="timing-card on-time">
                  <div className="timing-icon">✓</div>
                  <div className="timing-label">On Time (3-5 days)</div>
                  <div className="timing-count">{reportData.payment_timing.on_time}</div>
                </div>
                <div className="timing-card delay">
                  <div className="timing-icon">⏰</div>
                  <div className="timing-label">Delayed (6-7 days)</div>
                  <div className="timing-count">{reportData.payment_timing.delay}</div>
                </div>
                <div className="timing-card very-late">
                  <div className="timing-icon">⚠️</div>
                  <div className="timing-label">Very Late (&gt;7 days)</div>
                  <div className="timing-count">{reportData.payment_timing.very_late}</div>
                </div>
              </div>
            </div>

            {/* Payment Methods and Payment Link Side by Side */}
            <div className="pie-charts-row">
              {/* Payment Methods Breakdown */}
              <div className="analytics-section half-width">
                <h3>Payment Methods</h3>
                {reportData.payment_methods && reportData.payment_methods.length > 0 ? (
                  <div className="pie-chart-container">
                    <div className="pie-chart-wrapper">
                      <svg viewBox="0 0 200 200" className="pie-chart">
                        {(() => {
                          const total = reportData.payment_methods.reduce((sum, m) => sum + m.count, 0);
                          let currentAngle = -90; // Start from top
                          const colors = ['#153C7F', '#2563eb', '#0d9488', '#6366f1', '#475569', '#0891b2'];
                          
                          return reportData.payment_methods.map((method, index) => {
                            const percentage = (method.count / total) * 100;
                            const angle = (percentage / 100) * 360;
                            const startAngle = currentAngle;
                            const endAngle = currentAngle + angle;
                            
                            // Convert to radians
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = (endAngle * Math.PI) / 180;
                            
                            // Calculate arc path
                            const x1 = 100 + 80 * Math.cos(startRad);
                            const y1 = 100 + 80 * Math.sin(startRad);
                            const x2 = 100 + 80 * Math.cos(endRad);
                            const y2 = 100 + 80 * Math.sin(endRad);
                            
                            const largeArc = angle > 180 ? 1 : 0;
                            const pathData = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;
                            
                            currentAngle = endAngle;
                            
                            return (
                              <path
                                key={index}
                                d={pathData}
                                fill={colors[index % colors.length]}
                                stroke="white"
                                strokeWidth="2"
                              />
                            );
                          });
                        })()}
                      </svg>
                      <div className="pie-chart-legend">
                        {reportData.payment_methods.map((method, index) => {
                          const total = reportData.payment_methods.reduce((sum, m) => sum + m.count, 0);
                          const percentage = ((method.count / total) * 100).toFixed(1);
                          const colors = ['#153C7F', '#2563eb', '#0d9488', '#6366f1', '#475569', '#0891b2'];
                          
                          return (
                            <div key={index} className="legend-item">
                              <div className="legend-color" style={{ backgroundColor: colors[index % colors.length] }}></div>
                              <div className="legend-text">
                                <span className="legend-label">{method.method}</span>
                                <span className="legend-value">{method.count} ({percentage}%)</span>
                                <span className="legend-amount">{formatCurrency(method.amount)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-data">No payment method data available</div>
                )}
              </div>

              {/* Payment Provider Breakdown */}
              <div className="analytics-section half-width">
                <h3>Payment Providers</h3>
                {reportData.payment_providers && reportData.payment_providers.length > 0 ? (
                  <div className="pie-chart-container">
                    <div className="pie-chart-wrapper">
                      <svg viewBox="0 0 200 200" className="pie-chart">
                        {(() => {
                          const total = reportData.payment_providers.reduce((sum, p) => sum + p.count, 0);
                          let currentAngle = -90;
                          const colors = ['#153C7F', '#0d9488', '#6366f1', '#2563eb', '#475569', '#0891b2'];
                          
                          return reportData.payment_providers.map((provider, index) => {
                            const percentage = (provider.count / total) * 100;
                            const angle = (percentage / 100) * 360;
                            const startAngle = currentAngle;
                            const endAngle = currentAngle + angle;
                            
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = (endAngle * Math.PI) / 180;
                            
                            const x1 = 100 + 80 * Math.cos(startRad);
                            const y1 = 100 + 80 * Math.sin(startRad);
                            const x2 = 100 + 80 * Math.cos(endRad);
                            const y2 = 100 + 80 * Math.sin(endRad);
                            
                            const largeArc = angle > 180 ? 1 : 0;
                            const pathData = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;
                            
                            currentAngle = endAngle;
                            
                            return (
                              <path
                                key={index}
                                d={pathData}
                                fill={colors[index % colors.length]}
                                stroke="white"
                                strokeWidth="2"
                              />
                            );
                          });
                        })()}
                      </svg>
                      <div className="pie-chart-legend">
                        {reportData.payment_providers.map((provider, index) => {
                          const total = reportData.payment_providers.reduce((sum, p) => sum + p.count, 0);
                          const percentage = ((provider.count / total) * 100).toFixed(1);
                          const colors = ['#153C7F', '#0d9488', '#6366f1', '#2563eb', '#475569', '#0891b2'];
                          
                          return (
                            <div key={index} className="legend-item">
                              <div className="legend-color" style={{ backgroundColor: colors[index % colors.length] }}></div>
                              <div className="legend-text">
                                <span className="legend-label">{provider.provider}</span>
                                <span className="legend-value">{provider.count} ({percentage}%)</span>
                                <span className="legend-amount">{formatCurrency(provider.amount)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-data">No payment provider data available</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentAnalyticsReport;
